import type { Dispatch, ReactElement, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { QueryClient } from '@tanstack/react-query';
import type { NavigateFunction } from 'react-router-dom';
import {
  verifyExternalQuestsUserQuestsVerifyExternalQuestsPost,
} from '../../../client';
import type { DailyQuestAssignAndGetResponse, DailyQuestWithProgress, QuestObjective } from '../../../models/apiTypes';
import type { DailyQuestStartTarget, DailyQuestView } from '../../../models/quest';
import PlayTriangleIcon from '../../../components/PlayTriangleIcon';
import { showAlert } from '../../../utils/telegram/alert';
import { SHELL_ICON } from '../constants';
import type { NavigationConfirmDestination } from '../types';
import { cn } from '../../cn';
import DailyQuestCardStartActions from './DailyQuestCardStartActions';


const QUEST_GAME_JUMP_BTN_CLASS =
  'box-border flex min-h-[52px] w-full cursor-pointer items-center justify-center gap-[10px] rounded-[14px] border-2 border-accent bg-[linear-gradient(165deg,var(--color-constants-surface)_0%,var(--color-white)_42%,var(--color-constants-surface-2)_100%)] px-2xl py-[10px] shadow-[0_2px_0_var(--color-warning-deep-alpha-45),0_8px_20px_var(--color-accent-alpha-25),inset_0_1px_0_var(--color-white-alpha-90)]';

export interface DailyQuestCardRestActionsProps {
  quest: DailyQuestView;
  t: TFunction;
  queryClient: QueryClient;
  navigate: NavigateFunction;
  onClose: () => void;
  startedVerificationQuestIds: Set<number>;
  isVerifyingQuest: number | null;
  setIsVerifyingQuest: Dispatch<SetStateAction<number | null>>;
  setStartedVerificationQuestIds: Dispatch<SetStateAction<Set<number>>>;
  setCloudStorageItem: (key: string, value: string, callback?: (success: boolean) => void) => void;
  handleStartAction: (quest: Pick<DailyQuestView, 'questId' | 'userQuestId' | 'objectives'>) => Promise<void>;
  showingAdQuestIds: Set<number>;
  setNavigationConfirmModal: Dispatch<SetStateAction<{
    isOpen: boolean;
    destination: NavigationConfirmDestination;
  }>>;
  needsStartButton: (quest: DailyQuestStartTarget) => boolean;
}

export default function DailyQuestCardRestActions({
  quest,
  t,
  queryClient,
  navigate,
  onClose,
  startedVerificationQuestIds,
  isVerifyingQuest,
  setIsVerifyingQuest,
  setStartedVerificationQuestIds,
  setCloudStorageItem,
  handleStartAction,
  showingAdQuestIds,
  setNavigationConfirmModal,
  needsStartButton,
}: DailyQuestCardRestActionsProps): ReactElement | null {
// Invite Friends quest (quest_id 200)
if (quest.questId === 200) {
  if (quest.isRewardClaimed) {
    return null;
  }
  if (quest.isFinished) {
    return null;
  }
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClose();
        navigate("/menu", { state: { tab: "referral" } });
      }}
      className="flex w-full cursor-pointer items-center justify-center gap-md rounded-[34.925px] border border-accent bg-white px-3xl py-sm [font-family:Lato,sans-serif] text-[14px] font-bold text-accent shadow-card-raised"
    >
      {t("Invite my friends")}
    </button>
  );
}

// Спеціальна обробка для квесту підписки на Telegram (quest_id 6)
if (quest.questId === 6) {
  // Якщо винагорода вже забрана - не показуємо кнопку тут (буде показана в другому блоці)
  if (quest.isRewardClaimed) {
    return null;
  }
  
  // Якщо квест завершений - не показуємо кнопку "ПІДПИСАТИСЯ" (буде показана кнопка claim в другому блоці)
  if (quest.isFinished) {
    return null;
  }
  
  // Перевіряємо, чи була натиснута кнопка START
  const isStarted = !!(quest.userQuestId && startedVerificationQuestIds.has(quest.userQuestId));
  const isVerifying = isVerifyingQuest === quest.userQuestId;
  
  return (
    <button
      onClick={async (e) => {
        e.stopPropagation();
        if (isStarted && !isVerifying) {
          // Кнопка "Перевірити" - викликаємо API перевірки
          if (!quest.userQuestId) return;
          
          setIsVerifyingQuest(quest.userQuestId);
          try {
            const { data: response } = await verifyExternalQuestsUserQuestsVerifyExternalQuestsPost({
              body: { user_quest_ids: [quest.userQuestId] },
              throwOnError: true,
            });
            const updatedQuest = response.quests.find(q => q.user_quest_id === quest.userQuestId);
            
            if (updatedQuest?.is_finished) {
              // Квест виконано - оновлюємо кеш та показуємо кнопку claim
              const questKeys = ['dailyQuests', 'hourlyQuests', 'marketingQuests'] as const;
              questKeys.forEach((key) => {
                const currentData = queryClient.getQueryData<DailyQuestAssignAndGetResponse>([key]);
                if (!currentData?.quests) return;
                
                const updatedQuests = currentData.quests.map((q: DailyQuestWithProgress) => {
                  if (q.user_quest_id !== quest.userQuestId) return q;
                  return {
                    ...q,
                    objectives: updatedQuest.objectives,
                    is_finished: true,
                  };
                });
                
                queryClient.setQueryData<DailyQuestAssignAndGetResponse>([key], {
                  ...currentData,
                  quests: updatedQuests,
                });
              });
            } else {
              // Квест не виконано - повертаємо до стану "Перейти до"
              setStartedVerificationQuestIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(quest.userQuestId!);
                return newSet;
              });
              setCloudStorageItem(`quest_6_started`, 'false');
            }
          } catch (error) {
            console.error('[DailyQuestsModal] Failed to verify quest:', error);
            showAlert(t("Failed to verify quest. Please try again."));
          } finally {
            setIsVerifyingQuest(null);
          }
        } else if (!isStarted) {
          // Кнопка "FOLLOW" - відкриваємо посилання
          handleStartAction(quest);
        }
      }}
      disabled={isVerifying}
      className={cn(
        'flex w-full items-center justify-center gap-md rounded-[34.925px] border border-accent bg-white px-3xl py-sm [font-family:Lato,sans-serif] text-[14px] font-bold text-accent shadow-card-raised',
        isVerifying ? 'cursor-wait opacity-60' : 'cursor-pointer opacity-100',
      )}
    >
      {isVerifying ? (
        t("Verifying...")
      ) : isStarted ? (
        t("Verify")
      ) : (
        <>
          <img
            src="/ui/daily-quests/telegram-logo.png"
            alt="Telegram"
            className="h-5 w-5 object-contain"
          />
          {t("Visit group")}
        </>
      )}
    </button>
  );
}

// Спеціальна обробка для квесту підписки на Telegram чат (quest_id 108)
if (quest.questId === 108) {
  // Якщо винагорода вже забрана - не показуємо кнопку тут (буде показана в другому блоці)
  if (quest.isRewardClaimed) {
    return null;
  }
  
  // Якщо квест завершений - не показуємо кнопку "Join Chat" (буде показана кнопка claim в другому блоці)
  if (quest.isFinished) {
    return null;
  }
  
  // Перевіряємо, чи була натиснута кнопка START
  const isStarted = !!(quest.userQuestId && startedVerificationQuestIds.has(quest.userQuestId));
  const isVerifying = isVerifyingQuest === quest.userQuestId;
  
  return (
    <button
      onClick={async (e) => {
        e.stopPropagation();
        if (isStarted && !isVerifying) {
          // Кнопка "Перевірити" - викликаємо API перевірки
          if (!quest.userQuestId) return;
          
          setIsVerifyingQuest(quest.userQuestId);
          try {
            const { data: response } = await verifyExternalQuestsUserQuestsVerifyExternalQuestsPost({
              body: { user_quest_ids: [quest.userQuestId] },
              throwOnError: true,
            });
            const updatedQuest = response.quests.find(q => q.user_quest_id === quest.userQuestId);
            
            if (updatedQuest?.is_finished) {
              // Квест виконано - оновлюємо кеш та показуємо кнопку claim
              const questKeys = ['dailyQuests', 'hourlyQuests', 'marketingQuests'] as const;
              questKeys.forEach((key) => {
                const currentData = queryClient.getQueryData<DailyQuestAssignAndGetResponse>([key]);
                if (!currentData?.quests) return;
                
                const updatedQuests = currentData.quests.map((q: DailyQuestWithProgress) => {
                  if (q.user_quest_id !== quest.userQuestId) return q;
                  return {
                    ...q,
                    objectives: updatedQuest.objectives,
                    is_finished: true,
                  };
                });
                
                queryClient.setQueryData<DailyQuestAssignAndGetResponse>([key], {
                  ...currentData,
                  quests: updatedQuests,
                });
              });
            } else {
              // Квест не виконано - повертаємо до стану "Перейти до"
              setStartedVerificationQuestIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(quest.userQuestId!);
                return newSet;
              });
              setCloudStorageItem(`quest_108_started`, 'false');
            }
          } catch (error) {
            console.error('[DailyQuestsModal] Failed to verify quest:', error);
            showAlert(t("Failed to verify quest. Please try again."));
          } finally {
            setIsVerifyingQuest(null);
          }
        } else if (!isStarted) {
          // Кнопка "Join Chat" - відкриваємо посилання
          handleStartAction(quest);
        }
      }}
      disabled={isVerifying}
      className={cn(
        'flex w-full items-center justify-center gap-md rounded-[34.925px] border border-accent bg-white px-3xl py-sm [font-family:Lato,sans-serif] text-[14px] font-bold text-accent shadow-card-raised',
        isVerifying ? 'cursor-wait opacity-60' : 'cursor-pointer opacity-100',
      )}
    >
      {isVerifying ? (
        t("Verifying...")
      ) : isStarted ? (
        t("Verify")
      ) : (
        <>
          <img
            src="/ui/daily-quests/telegram-logo.png"
            alt="Telegram"
            className="h-5 w-5 object-contain"
          />
          {t("Visit chat")}
        </>
      )}
    </button>
  );
}

// Спеціальна обробка для квесту написання боту (quest_id 172)
if (quest.questId === 172) {
  // Якщо винагорода вже забрана - не показуємо кнопку тут (буде показана в другому блоці)
  if (quest.isRewardClaimed) {
    return null;
  }
  
  // Якщо квест завершений - не показуємо кнопку переходу (буде показана кнопка claim в другому блоці)
  if (quest.isFinished) {
    return null;
  }
  
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        handleStartAction(quest);
      }}
      className={QUEST_GAME_JUMP_BTN_CLASS}
    >
      <img
        src="/ui/daily-quests/telegram-logo.png"
        alt=""
        className="h-[26px] w-[26px] object-contain"
      />
      <PlayTriangleIcon size={12} color={'var(--color-accent)'} />
    </button>
  );
}

// Спеціальна обробка для квесту дуелей (quest_id 207)
if (quest.questId === 207) {
  // Якщо винагорода вже забрана - не показуємо кнопку тут (буде показана в другому блоці)
  if (quest.isRewardClaimed) {
    return null;
  }
  
  // Якщо квест завершений - не показуємо кнопку переходу (буде показана кнопка claim в другому блоці)
  if (quest.isFinished) {
    return null;
  }
  
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
        navigate('/tap', { state: { openDuel: true } });
      }}
      className={QUEST_GAME_JUMP_BTN_CLASS}
    >
      <span className="text-[28px] leading-none" aria-hidden>⚔️</span>
      <PlayTriangleIcon size={12} color={'var(--color-accent)'} />
    </button>
  );
}

// Спеціальна обробка для квестів відвідування локацій (City Hall, Shop, Hall of Fame)
const visitObjective = quest.objectives?.find((obj: QuestObjective) => obj.type === 'visit_static_map_object');
if (visitObjective) {
  const targetAction = visitObjective.target_action;
  let locationIcon = '';
  let locationPath = '';
  
  if (targetAction === 'visit_city_hall') {
    locationIcon = '/game-assets/map-objects/cityhall.webp';
    locationPath = '/map';
  } else if (targetAction === 'visit_shop') {
    locationIcon = '/game-assets/map-objects/shop.webp';
    locationPath = '/shop';
  } else if (targetAction === 'visit_hell_of_flame') {
    locationIcon = '/game-assets/map-objects/hall-of-fame.webp';
    locationPath = '/top';
  }
  
  if (locationIcon && locationPath) {
    // Якщо винагорода вже забрана - не показуємо кнопку тут (буде показана в другому блоці)
    if (quest.isRewardClaimed) {
      return null;
    }
    
    // Якщо квест завершений - не показуємо кнопку переходу (буде показана кнопка claim в другому блоці)
    if (quest.isFinished) {
      return null;
    }
    
    const getDestination = (): 'shop' | 'hall_of_fame' | 'city_hall' => {
      if (targetAction === 'visit_city_hall') return 'city_hall';
      if (targetAction === 'visit_shop') return 'shop';
      return 'hall_of_fame';
    };

    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setNavigationConfirmModal({ isOpen: true, destination: getDestination() });
        }}
        className={QUEST_GAME_JUMP_BTN_CLASS}
      >
        <img
          src={locationIcon}
          alt=""
          className="h-8 w-8 object-contain"
        />
        <PlayTriangleIcon size={12} color={'var(--color-accent)'} />
      </button>
    );
  }
}

// Спеціальна обробка для квестів виборів мера (139, 140, 141) — іконка ратуші + play.
// Shown whenever the quest is not finished, even if a reward was claimed earlier:
// a new election cycle resets is_finished but keeps reward_claimed from the
// previous cycle, and such a card used to show no way to reach City Hall at all.
if ((quest.questId === 139 || quest.questId === 140 || quest.questId === 141) && !quest.isFinished) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setNavigationConfirmModal({ isOpen: true, destination: 'city_hall' });
      }}
      className={QUEST_GAME_JUMP_BTN_CLASS}
    >
      <img
        src="/game-assets/map-objects/cityhall.webp"
        alt=""
        className="h-8 w-8 object-contain"
      />
      <PlayTriangleIcon size={12} color={'var(--color-accent)'} />
    </button>
  );
}

// Спеціальна обробка для квестів тапінгу (quest_id 5, 106, 107)
if (quest.questId === 5 || quest.questId === 106 || quest.questId === 107) {
  // Якщо винагорода вже забрана - не показуємо кнопку тут (буде показана в другому блоці)
  if (quest.isRewardClaimed) {
    return null;
  }
  
  // Якщо квест завершений - не показуємо кнопку переходу (буде показана кнопка claim в другому блоці)
  if (quest.isFinished) {
    return null;
  }
  
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        handleStartAction(quest);
      }}
      className={QUEST_GAME_JUMP_BTN_CLASS}
    >
      <img
        src={SHELL_ICON}
        alt=""
        className="h-[30px] w-[30px] object-contain"
      />
      <PlayTriangleIcon size={12} color={'var(--color-accent)'} />
    </button>
  );
}

  return (
    <DailyQuestCardStartActions
      quest={quest}
      t={t}
      queryClient={queryClient}
      startedVerificationQuestIds={startedVerificationQuestIds}
      isVerifyingQuest={isVerifyingQuest}
      setIsVerifyingQuest={setIsVerifyingQuest}
      setStartedVerificationQuestIds={setStartedVerificationQuestIds}
      setCloudStorageItem={setCloudStorageItem}
      handleStartAction={handleStartAction}
      showingAdQuestIds={showingAdQuestIds}
      needsStartButton={needsStartButton}
    />
  );
}
